import React, { useCallback } from 'react';
import type { SceneAlternate } from '../../api/clip';
import { constructMediaUrl } from '../../api/helpers';
import { mediaThumbUrl } from '../../api/mediaUrls';
import { EditorMedia } from './editorStore';
import { MUTED_LABEL } from './editorStyles';
import useSceneAlternates, { SceneAlternatesState } from './useSceneAlternates';

/**
 * The other times this scene was generated.
 *
 * A pipeline scene is rarely generated once: a regeneration PREPENDS an
 * attachment to its checkpoint rather than replacing one, so by the time a clip
 * is assembled the run is holding every take of every scene — and the assembly
 * silently picked the newest. Until now the only way back to an earlier take was
 * to pay for a fresh one, which is both slower and a different frame than the
 * one the user remembers liking.
 *
 * So: the takes themselves, as thumbnails, newest first, the bound one marked.
 * Picking rebinds the clip's media ref (the same `swapClipMedia` call "Replace"
 * makes from the media library) and reloads the slide. Nothing is generated,
 * nothing is charged, nothing is deleted — the take that was bound a moment ago
 * is still in the strip, so the pick is always reversible.
 *
 * Two sources feed it, and they bind DIFFERENTLY:
 *
 *   - `origin: 'take'` is a candidate render of this very item — the
 *     review_variants ledger the AI reviewer and the user's own Regenerate both
 *     write to. Picking one goes through `selectVariant`, which repoints the
 *     item's bytes and KEEPS its id, so clip media_refs, the storyboard and
 *     every render template stay valid.
 *   - `origin: 'run'` is a pipeline attachment: a different media item. Picking
 *     one goes through `swapClipMedia`, which rebinds the clip to that id.
 *
 * Using swap for a take would change the item id under a render template that
 * names the old one; using select-variant for a run attachment would fail,
 * because it is not on this item's ledger. So the row says which, and this
 * component does what it says.
 *
 * Fetching, filtering and picking live in `useSceneAlternates`; this component
 * only paints. It renders nothing at all when the scene has no history — a
 * clip built by hand, or assembled before the provenance stamps existed,
 * resolves no run and has no ledger, so the backend answers with an empty
 * list. A FAILED lookup is different and shows as a one-line retry: silence
 * there used to hide real takes behind any backend hiccup.
 */

export interface SceneAlternatesStripProps {
  clipId: string;
  /** The selected block. Its `kind` decides which takes are offered. */
  slide: EditorMedia;
  /** Position of the slide's item in its clip bucket — the swap's legacy fallback. */
  mediaIndex: number;
  /**
   * Runs after a successful pick, with the media id now bound to the block.
   *
   * For a take that is the block's OWN id — select-variant never changes it —
   * so a caller re-selecting on this value is a no-op rather than a jump.
   */
  onPicked: (boundMediaId: string) => void | Promise<void>;
  /**
   * Pre-fetched takes state from a parent that hoisted `useSceneAlternates`
   * (the storyboard panel shares one instance between this strip and the
   * poster's arrows overlay). Absent, the strip runs its own — the editor's
   * side panel mounts it standalone and needs no second surface.
   */
  state?: SceneAlternatesState;
}

/**
 * What a tile says it is. The ledger's own label is the honest one where there
 * is one — "the original" is a different claim from "take 3" — and everything
 * else falls back to its position in the strip.
 */
const takeLabel = (alternate: SceneAlternate, position: number): string => {
  if (alternate.origin === 'take' && alternate.take_source === 'original') return 'the original take';
  if (alternate.origin === 'run') return `generation ${position + 1}`;
  return `take ${position + 1}`;
};

const SceneAlternatesStrip: React.FC<SceneAlternatesStripProps> = ({
  clipId,
  slide,
  mediaIndex,
  onPicked,
  state,
}) => {
  // Called unconditionally (hooks must be), disarmed when the parent already
  // holds the state — otherwise strip + overlay would fetch the list twice.
  const ownState = useSceneAlternates({ clipId, slide, mediaIndex, onPicked, enabled: !state });
  const { takes, pending, error, retry, pick } = state ?? ownState;

  const slideId = slide.id;

  /**
   * A take's ORIGINAL bytes, cache-busted the same way its thumb is.
   *
   * Both raw-URL leaves route through here: the `onError` fallback, and the
   * implicit-current head, whose url is the block's own `fileUrl`. Only the
   * bound take can have been rewritten in place (a regenerate keeps the id),
   * so it is the only one with a version worth appending — the same rule
   * `mediaThumbUrl` is given below. `constructMediaUrl` is the single appender
   * for all of it, so nothing can end up busted twice.
   */
  const takeOriginalUrl = useCallback(
    (alternate: SceneAlternate): string =>
      constructMediaUrl(
        alternate.url,
        alternate.media_id === slideId ? slide.contentVersion : undefined
      ),
    [slideId, slide.contentVersion]
  );

  if (error) {
    // Muted and one line on purpose: this panel is an extra affordance and
    // must never be able to break the inspector around it — but it may no
    // longer pretend a failure is an empty history either.
    return (
      <div
        className="border-t border-[var(--border-subtle)] px-3 py-2"
        data-testid="clip-editor-alternates-error"
      >
        <span className={MUTED_LABEL}>
          Couldn't load takes —{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-zinc-300"
            onClick={retry}
          >
            retry
          </button>
        </span>
      </div>
    );
  }

  if (takes.length === 0) return null;

  return (
    <div
      className="border-t border-[var(--border-subtle)] px-3 py-3"
      data-testid="clip-editor-alternates"
    >
      <div className="mb-1.5 flex items-center justify-between">
        <span className={MUTED_LABEL}>Alternates</span>
        <span className={MUTED_LABEL}>{takes.length} takes</span>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {takes.map((alternate, position) => (
          <button
            key={alternate.media_id}
            type="button"
            aria-pressed={alternate.current}
            aria-label={
              alternate.current
                ? `${takeLabel(alternate, position)}, currently used`
                : `Use ${takeLabel(alternate, position)}`
            }
            title={takeLabel(alternate, position)}
            disabled={Boolean(pending)}
            data-testid={`clip-editor-alternate-${alternate.media_id}`}
            className={`relative h-14 w-14 shrink-0 overflow-hidden border bg-black transition-colors disabled:opacity-40 ${
              alternate.current
                ? 'border-[var(--accent-strong)]'
                : 'border-[var(--border-subtle)] hover:border-white/40'
            }`}
            onClick={() => void pick(alternate)}
          >
            {/* What the tile falls back to: a take number, which is what the
                strip is ordered by anyway. It sits UNDER the thumb, so a
                derivative that never warmed leaves a numbered square rather
                than a broken-image glyph. */}
            <span className={`absolute inset-0 flex items-center justify-center ${MUTED_LABEL}`}>
              {position + 1}
            </span>
            <img
              src={mediaThumbUrl(
                alternate.media_id,
                // Only the bound take needs busting: a regenerate repoints
                // the canonical item at new bytes under the SAME id, so its
                // thumbnail derivative is the one cached under a URL that did
                // not change. Every other row is an immutable take.
                alternate.media_id === slideId ? slide.contentVersion : undefined
              )}
              alt=""
              className="relative h-full w-full object-cover"
              onError={(event) => {
                const img = event.currentTarget;
                // The webp derivative is warmed lazily and 404s until it
                // exists. An image's original still paints; a video's is an
                // mp4, which in an `<img>` can only ever paint alt text — so
                // that one uncovers the number instead.
                //
                // The retry is tracked on the element rather than by comparing
                // `img.src` to the candidate: the browser reports `src`
                // resolved to an absolute URL, so comparing it against a
                // relative `/api/...` never matched and the hide branch — for
                // an original that 404s too — was unreachable.
                const original = alternate.type === 'image' ? takeOriginalUrl(alternate) : '';
                if (original && img.dataset.originalTried !== 'yes') {
                  img.dataset.originalTried = 'yes';
                  img.src = original;
                  return;
                }
                img.style.visibility = 'hidden';
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default SceneAlternatesStrip;
